import React, { useState, useEffect } from 'react';
import { ArrowLeft, Clock, User, Calendar, ChevronLeft, ChevronRight, Share2, Twitter, Facebook, Linkedin, Play, Loader2 } from 'lucide-react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import VODFooter from '../components/VODFooter';
import { blogArticles as staticArticles } from '../data/blogArticles';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FONT_MAP = {
  'default': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  'serif': 'Georgia, "Times New Roman", serif',
  'sans': '"Inter", "Helvetica Neue", Arial, sans-serif',
  'mono': '"Fira Code", "Courier New", monospace',
  'playfair': '"Playfair Display", Georgia, serif',
  'merriweather': '"Merriweather", Georgia, serif',
  'lora': '"Lora", Georgia, serif',
  'poppins': '"Poppins", "Helvetica Neue", sans-serif',
};

const FONT_LINKS = {
  'playfair': 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&display=swap',
  'merriweather': 'https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&display=swap',
  'lora': 'https://fonts.googleapis.com/css2?family=Lora:wght@400;700&display=swap',
  'poppins': 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap',
};

// Video Embed Component
const VideoEmbed = ({ video, accent }) => {
  if (!video) return null;
  const accentColor = accent || '#a855f7';
  
  return (
    <div className="my-8 rounded-xl overflow-hidden border-2" style={{ borderColor: accentColor + '40' }}>
      {video.video_url ? (
        <div className="relative aspect-video bg-black">
          <iframe
            src={`https://customer-lw0eqy5vwq.cloudflarestream.com/${video.video_url}/iframe?poster=${encodeURIComponent(video.thumbnail_url || '')}`}
            className="w-full h-full"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : video.thumbnail_url ? (
        <Link to={`/vod/video/${video.slug}`} className="block relative aspect-video bg-black group">
          <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center bg-black/60 group-hover:scale-110 transition-transform" style={{ borderColor: accentColor, border: '2px solid' }}>
              <Play className="h-7 w-7 ml-1" style={{ color: accentColor }} />
            </div>
          </div>
        </Link>
      ) : null}
      <div className="p-4" style={{ backgroundColor: accentColor + '10' }}>
        <Link to={`/vod/video/${video.slug}`} className="font-semibold hover:underline" style={{ color: accentColor }}>
          {video.title}
        </Link>
        <p className="text-sm text-gray-400 mt-1">Watch on LTV VOD</p>
      </div>
    </div>
  );
};

const VODBlogPost = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState({});
  
  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        // Fetch from DB
        const [articleRes, allRes] = await Promise.all([
          axios.get(`${API}/api/blog/articles/slug/${slug}`),
          axios.get(`${API}/api/blog/articles?published_only=true`),
        ]);
        setArticle(articleRes.data);
        setAllArticles(allRes.data || []);
        
        // Fetch embedded videos
        const videoIds = [articleRes.data.featured_video_id, ...(articleRes.data.embedded_video_ids || [])].filter(Boolean);
        if (videoIds.length > 0) {
          const videosRes = await axios.get(`${API}/api/blog/videos-for-embed`);
          const videoMap = {};
          (videosRes.data.videos || []).forEach(v => { videoMap[v.id] = v; });
          setVideos(videoMap);
        }
      } catch {
        // Fallback to static
        const staticArticle = staticArticles.find(a => a.slug === slug);
        if (staticArticle) {
          setArticle({
            ...staticArticle,
            excerpt: staticArticle.excerpt,
            read_time: staticArticle.readTime,
            created_at: staticArticle.date,
          });
        }
        setAllArticles(staticArticles.map(a => ({ ...a, read_time: a.readTime, created_at: a.date })));
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }
  
  if (!article) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
        <Link to="/vod/blog" className="text-purple-400 hover:text-purple-300">Back to Blog</Link>
      </div>
    );
  }
  
  // Design
  const d = article.design || {};
  const bgColor = d.page_bg_color || '#000000';
  const titleColor = d.title_color || '#ffffff';
  const textColor = d.text_color || '#d1d5db';
  const accentColor = d.accent_color || '#a855f7';
  const headingColor = d.heading_color || '#ffffff';
  const linkColor = d.link_color || '#a855f7';
  const fontFamily = FONT_MAP[d.font_family] || FONT_MAP['default'];
  const fontLink = FONT_LINKS[d.font_family];
  
  // SEO
  const s = article.seo || {};
  const metaTitle = s.seo_title || article.title;
  const metaDesc = s.seo_description || article.excerpt;
  const ogImage = (() => {
    const raw = s.og_image || article.image;
    if (!raw) return 'https://ltvproduction.com/api/seo/og-image';
    if (raw.startsWith('/api/seo/og-image')) return raw;
    return `https://ltvproduction.com/api/seo/og-image-proxy?url=${encodeURIComponent(raw)}`;
  })();
  const canonical = s.canonical_url || `https://ltvproduction.com/vod/blog/${slug}`;
  const keywords = s.seo_keywords || '';
  const focusKeyword = s.focus_keyword || '';
  
  // Navigation
  const currentIndex = allArticles.findIndex(a => a.slug === slug);
  const prevArticle = currentIndex > 0 ? allArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < allArticles.length - 1 ? allArticles[currentIndex + 1] : null;
  const relatedArticles = allArticles.filter(a => a.category === article.category && a.slug !== slug).slice(0, 3);
  
  const shareUrl = `https://ltvproduction.com/vod/blog/${slug}`;
  const featuredVideo = article.featured_video_id ? videos[article.featured_video_id] : null;
  const embeddedVids = (article.embedded_video_ids || []).map(id => videos[id]).filter(Boolean);
  
  // Is the background light or dark?
  const isLightBg = isLightColor(bgColor);
  const borderColor = isLightBg ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)';
  
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: bgColor, color: textColor, fontFamily }} data-testid="blog-post-page">
      {fontLink && (
        <Helmet>
          <link rel="stylesheet" href={fontLink} />
        </Helmet>
      )}
      <Helmet>
        <title>{metaTitle} | LTV VOD Blog</title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="article:published_time" content={article.created_at} />
        <meta property="article:author" content={article.author} />
        {focusKeyword && <meta name="news_keywords" content={focusKeyword} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      {/* Header */}
      <header style={{ borderBottom: `1px solid ${borderColor}` }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 transition-colors hover:opacity-80" style={{ color: textColor }}>
            <ArrowLeft className="h-5 w-5" /> Back
          </button>
          <Link to="/vod" className="flex items-center gap-2">
            <img src="/ltv-icon.png" alt="LTV VOD" className="h-8 w-8" style={{ filter: isLightBg ? 'none' : 'brightness(0) invert(1)' }} />
            <span className="font-bold" style={{ color: titleColor }}>LTV VOD</span>
          </Link>
        </div>
      </header>

      {/* Article Content */}
      <main className="flex-1">
        <article className="max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="mb-6">
            <ol className="flex items-center gap-2 text-sm" style={{ color: textColor + '99' }}>
              <li><Link to="/vod" className="hover:opacity-80">Home</Link></li>
              <li>/</li>
              <li><Link to="/vod/blog" className="hover:opacity-80">Blog</Link></li>
              <li>/</li>
              <li className="truncate max-w-[200px] opacity-60">{article.title}</li>
            </ol>
          </nav>

          {/* Category */}
          <span className="inline-block text-sm font-medium px-3 py-1 rounded-full mb-4" style={{ backgroundColor: accentColor + '20', color: accentColor }}>
            {article.category}
          </span>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: titleColor }}>
            {article.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm mb-8 pb-8" style={{ color: textColor + '99', borderBottom: `1px solid ${borderColor}` }}>
            <span className="flex items-center gap-2"><User className="h-4 w-4" /> {article.author}</span>
            <span className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {new Date(article.created_at || article.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> {article.read_time || article.readTime}</span>
          </div>

          {/* Featured Image */}
          {article.image && !article.hide_featured_image && (
            <div className="aspect-video rounded-xl overflow-hidden mb-10">
              <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
            </div>
          )}

          {/* Featured Video */}
          {featuredVideo && <VideoEmbed video={featuredVideo} accent={accentColor} />}

          {/* Article Body with custom styling */}
          <style>{`
            .custom-article h1, .custom-article h2, .custom-article h3, .custom-article h4 { color: ${headingColor}; font-weight: 700; }
            .custom-article h2 { font-size: 1.5rem; margin-top: 2rem; margin-bottom: 0.75rem; }
            .custom-article h3 { font-size: 1.25rem; margin-top: 1.5rem; margin-bottom: 0.5rem; }
            .custom-article p { color: ${textColor}; line-height: 1.75; margin-bottom: 1rem; }
            .custom-article a { color: ${linkColor}; text-decoration: underline; }
            .custom-article a:hover { opacity: 0.8; }
            .custom-article strong { color: ${headingColor}; }
            .custom-article ul, .custom-article ol { color: ${textColor}; padding-left: 1.5rem; margin-bottom: 1rem; }
            .custom-article li { margin-bottom: 0.25rem; line-height: 1.75; }
            .custom-article blockquote { border-left: 3px solid ${accentColor}; padding-left: 1rem; color: ${textColor}99; font-style: italic; margin: 1.5rem 0; }
            .custom-article code { color: ${accentColor}; background: ${accentColor}15; padding: 0.15rem 0.4rem; border-radius: 0.25rem; font-size: 0.875em; }
            .custom-article pre { background: ${isLightBg ? '#f3f4f6' : '#111827'}; padding: 1rem; border-radius: 0.5rem; overflow-x: auto; margin: 1rem 0; }
            .custom-article hr { border-color: ${borderColor}; margin: 2rem 0; }
          `}</style>
          <div className="custom-article max-w-none text-lg">
            <ReactMarkdown>{article.content}</ReactMarkdown>
          </div>

          {/* Embedded Videos */}
          {embeddedVids.length > 0 && (
            <div className="mt-10">
              <h3 className="text-xl font-bold mb-4" style={{ color: headingColor }}>Related Videos</h3>
              {embeddedVids.map(v => <VideoEmbed key={v.id} video={v} accent={accentColor} />)}
            </div>
          )}

          {/* Share Section */}
          <div className="mt-12 pt-8" style={{ borderTop: `1px solid ${borderColor}` }}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2" style={{ color: textColor + '99' }}>
                <Share2 className="h-5 w-5" /> <span>Share this article:</span>
              </div>
              <div className="flex items-center gap-3">
                {[
                  { icon: Twitter, url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`, label: 'Twitter' },
                  { icon: Facebook, url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, label: 'Facebook' },
                  { icon: Linkedin, url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, label: 'LinkedIn' },
                ].map(s => (
                  <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg transition-colors hover:opacity-80" style={{ backgroundColor: isLightBg ? '#e5e7eb' : '#1f2937' }} aria-label={`Share on ${s.label}`}>
                    <s.icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Prev/Next */}
          <div className="mt-12 pt-8 grid md:grid-cols-2 gap-4" style={{ borderTop: `1px solid ${borderColor}` }}>
            {prevArticle ? (
              <Link to={`/vod/blog/${prevArticle.slug}`} className="group p-4 rounded-xl transition-colors" style={{ backgroundColor: isLightBg ? '#f3f4f6' : '#111827', border: `1px solid ${borderColor}` }}>
                <span className="flex items-center gap-1 text-sm mb-2" style={{ color: textColor + '80' }}><ChevronLeft className="h-4 w-4" /> Previous Article</span>
                <span className="font-medium line-clamp-2" style={{ color: headingColor }}>{prevArticle.title}</span>
              </Link>
            ) : <div />}
            {nextArticle && (
              <Link to={`/vod/blog/${nextArticle.slug}`} className="group p-4 rounded-xl text-right transition-colors" style={{ backgroundColor: isLightBg ? '#f3f4f6' : '#111827', border: `1px solid ${borderColor}` }}>
                <span className="flex items-center justify-end gap-1 text-sm mb-2" style={{ color: textColor + '80' }}>Next Article <ChevronRight className="h-4 w-4" /></span>
                <span className="font-medium line-clamp-2" style={{ color: headingColor }}>{nextArticle.title}</span>
              </Link>
            )}
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12" style={{ backgroundColor: isLightBg ? '#f9fafb' : '#0a0a0a' }}>
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="text-2xl font-bold mb-8" style={{ color: headingColor }}>Related Articles</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedArticles.map(related => (
                  <Link key={related.slug || related.id} to={`/vod/blog/${related.slug}`} className="group rounded-xl overflow-hidden transition-colors" style={{ backgroundColor: isLightBg ? '#ffffff' : '#111827', border: `1px solid ${borderColor}` }}>
                    <div className="aspect-video overflow-hidden">
                      <img src={related.image} alt={related.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold line-clamp-2" style={{ color: headingColor }}>{related.title}</h3>
                      <p className="text-sm mt-2" style={{ color: textColor + '80' }}>{related.read_time || related.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center rounded-2xl p-8" style={{ background: `linear-gradient(135deg, ${accentColor}15, ${accentColor}08)`, border: `1px solid ${accentColor}30` }}>
              <h2 className="text-2xl font-bold mb-4" style={{ color: headingColor }}>Explore More Content</h2>
              <p className="mb-6" style={{ color: textColor + '99' }}>Discover exclusive videos, live events, and creator content on LTV VOD.</p>
              <Link to="/vod" className="inline-flex items-center gap-2 text-white px-6 py-3 rounded-lg transition-colors hover:opacity-90" style={{ backgroundColor: accentColor }}>
                Start Watching <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      <VODFooter />
    </div>
  );
};

function isLightColor(hex) {
  const c = hex.replace('#', '');
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 128;
}

export default VODBlogPost;
